import { t } from "@lingui/core/macro";
import { clsx, EMPTY_STRING, KeyNames, mergeRefs, queueMacrotask } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import {
  RegrelloButton,
  type RegrelloButtonProps,
  RegrelloCommandEmpty,
  RegrelloCommandGroup,
  RegrelloCommandInput,
  RegrelloCommandItem,
  RegrelloCommandList,
  RegrelloCommandLoading,
  RegrelloCommandRoot,
  RegrelloField,
  RegrelloIcon,
  RegrelloPopover,
  type RegrelloPopoverProps,
  type RegrelloSize,
  RegrelloSpinner,
  RegrelloTooltippedText,
} from "@regrello/ui-core";
import React, { Fragment, type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { RegrelloFormFieldBaseProps } from "./_internal/RegrelloFormFieldBaseProps";
import { RegrelloFormFieldEmptyValueNone } from "./_internal/RegrelloFormFieldEmptyValueNone";
import { RegrelloFormFieldLayout } from "./_internal/RegrelloFormFieldLayout";
import { RegrelloSelectV2LoadingOption } from "./_internal/selectOptions/RegrelloSelectV2LoadingOption";
import { getSelfContainedFormInternal } from "./_internal/selfContainedForm/getSelfContainedFormInternal";
import { useOptimalPopoverSide } from "./select/useOptimalPopoverSide";

const DEFAULT_GROUP = "__DEFAULT_GROUP__";

export type RegrelloSelectChangeReason = "create-option" | "select-option" | "remove-option" | "clear" | "blur";

type GroupedOptions<T> = Record<string, { heading: React.ReactNode; options: T[]; extraEndOptions: ReactNode[] }>;

export interface RegrelloFormFieldSelectProps<T> extends RegrelloFormFieldBaseProps<T | null> {
  /**
   * Where along the specified {@see side} of the target to align the menu.
   *
   * @default "start"
   */
  align?: RegrelloPopoverProps["align"];

  /**
   * Should field be auto focused on mount.
   */
  autoFocus?: boolean;

  /**
   * Data test ID attached to search input. Handy to get the input state.
   */
  dataTestIdForMenuInput?: string;

  /**
   * Extra options attached to the bottom of the option list, that will be force mounted (not-filterable).
   */
  extraEndOptions?: ReactNode[];

  /**
   * Extra options attached to the bottom of the list for each group, that will be force mounted
   * (not-filterable).
   */
  extraEndOptionsByGroupKey?: Record<string, ReactNode[]>;

  /**
   * Callback to determine if option matches filter condition.
   */
  filterOption?: (option: T) => boolean;

  /**
   * Callback to determine if option is disabled, with an option to return a reason for the disabled
   * state that will be rendered as a tooltip.
   */
  getOptionDisabled?: (option: T) => boolean;

  /**
   * Callback to render option if option is not a plain string.
   */
  getOptionLabel: (option: T) => string;

  /**
   * Function returning a tooltip that should appear when hovering over the option.
   */
  getOptionTooltip?: (option: T) => React.ReactNode | undefined;

  /**
   * Callback that should be used to return option value.
   * Should be used when `getOptionLabel` can return the same value for multiple options.
   */
  getOptionValue?: (option: T) => string;

  /**
   * Callback that should return group name for a given option.
   */
  groupBy?: (option: T) => string;

  /**
   * Whether to highlight the button with a blue outline when the command menu is open.
   *
   * @default false
   */
  highlightButtonInOpenState?: boolean;

  /** Props to pass to the input element. */
  inputProps?: Omit<React.HTMLProps<HTMLInputElement>, "value"> & {
    value?: string;
  };

  /**
   * Ref attached to the search input.
   */
  inputRef?: React.Ref<HTMLInputElement>;

  /**
   * Whether the input show show an "x" button.
   *
   * @default false
   */
  isClearButtonEnabled?: boolean;

  /**
   * Whether to hide the form field error icon and message. Added (potentially temporarily depending
   * on Design System updates) for Conditional Branching to support the new error state for stage
   * start date forms.
   */
  isErrorMessageHidden?: boolean;

  /**
   * Whether to allow typing to filter options. If `true`, an input will be rendered in the menu.
   *
   * @default true
   */
  isFilterable?: boolean;

  /**
   * Should render loading placeholder instead of options list.
   */
  isLoading?: boolean;

  /**
   * Whether additional options are being loaded in the background. Use this instead of `isLoading`
   * when there are existing options to which the loading options will be appended. Renders a
   * loading placeholder option at the end of the existing options.
   *
   * @default false
   */
  isLoadingMore?: boolean;

  /**
   * Whether to disable built-in fuzzy-match filtering.
   *
   * @default true
   */
  isServerFiltered?: boolean;

  /**
   * Whether server side searching is enabled. In this case, the drop down won't include the input
   * box for frontend filtering.
   */
  isServerSearchEnabled?: boolean;

  /**
   * Whether to open a tooltip showing the full content of value on hover.
   *
   * @default false
   */
  isTooltipEnabled?: boolean | ((option: T | null) => boolean);

  /**
   * Calback triggered on select button blur.
   */
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;

  /**
   * Callback invoked when selected option change.
   */
  onChange: (newValue: T | null, reason: RegrelloSelectChangeReason) => void;

  /**
   * Callback for defining extra actions to be performed before the value is cleared.
   */
  onClearClick?: () => void;

  /**
   * Callback invoked when the menu closes.
   */
  onClose?: () => void;

  /**
   * Callback invoked when the input value changes.
   */
  onInputValueChange?: (value: string) => void;

  /**
   * Callback invoked when the menu opens.
   */
  onOpen?: () => void;

  /**
   * Callback invoked when the menu scrolls. This is useful for using infinite scrolling to reduce
   * data requested.
   */
  onScroll?: ({ currentTarget }: { currentTarget: HTMLElement }) => Promise<void>;

  /**
   * If true, opens the popover on mount so the search and list are visible by default.
   *
   * @default false
   */
  openOnMount?: boolean;

  /**
   * Available options list.
   */
  options: T[];

  /**
   * Placeholder text displayed when nothing is selected.
   */
  placeholder?: string;

  /**
   * Custom content to show above the list of items in the popover. A common use case here for
   * Regrello would be tabs.
   *
   * ___Note:___ By default, clicks in the popover automatically refocus the chip input outside of
   * the popover. You can override this by invoking `event.preventDefault()` in your mouse- and
   * keyboard-event handlers on this custom element, if you wish.
   */
  popoverCustomTopContent?: React.ReactNode;

  /**
   * Callback invoked to render a heading for a given group name. If not defined or if the callback
   * returns `undefined` or `null`, the group name will be used by default instead.
   */
  renderGroupHeading?: (groupName: string) => React.ReactNode;

  /**
   * Callback that should return rendered option when more complex structure is needed.
   */
  renderOption?: (option: T) => ReactNode;

  /**
   * Callback used to render the selected option. It might be different than rendered element in the popover.
   */
  renderSelectedValue?: (option: T | null) => React.ReactNode;

  /** Ref to pass to the button/trigger element. */
  selectRef?: React.Ref<HTMLButtonElement>;

  /**
   * The size of the input field.
   *
   * @default RegrelloSize.MEDIUM
   */
  size?: RegrelloSize;

  /**
   * Whether to suppress the default helper text below the field input.
   *
   * @default false
   */
  suppressHelperTextBelowFieldInput?: boolean;

  /**
   * Whether to _not_ render any padding around each option. Useful if you want to handle padding
   * in the element returned from `renderOption`.
   *
   * @default false
   */
  suppressOptionPadding?: boolean;

  /**
   * Whether to suppress the selection of the given search input value on open. If true, the cursor
   * is just moved to the end of the input instead of selecting the value.
   *
   * @default false
   */
  suppressSelectValueOnOpen?: boolean;

  /**
   * Custom props to change the visual styles of the trigger button. Pass `endIcon={null}` to hide
   * the default chevron end icon.
   */
  triggerButtonProps?: Pick<RegrelloButtonProps, "dataTestId" | "intent" | "onClick" | "variant"> & {
    endIcon?: null | undefined;
  };

  /**
   * Currently selected option.
   */
  value: T | null;
}

/**
 * A form field that allows selection of a single value from a menu, with optional support for
 * filtering within the menu.
 */
export function RegrelloFormFieldSelect<T>({
  align = "start",
  autoFocus,
  className,
  dataTestId,
  disabled,
  error,
  endAdornment,
  extraEndOptions = [],
  extraEndOptionsByGroupKey,
  filterOption: propsFilterOptions,
  getOptionDisabled,
  getOptionLabel,
  getOptionValue,
  getOptionTooltip,
  groupBy,
  helperText,
  highlightButtonInOpenState = false,
  infoTooltipText,
  infoTooltipIconName,
  infoTooltipVariant,
  inputRef,
  inputProps,
  isClearButtonEnabled = false,
  isDefaultMarginsOmitted,
  isDeleted,
  isEmphasized,
  isErrorMessageHidden = false,
  isFilterable = true,
  isServerFiltered = false,
  isRequiredAsteriskShown,
  isServerSearchEnabled = false,
  label,
  labelPlacement,
  labelWidth,
  isLoading,
  isLoadingMore = false,
  name,
  onBlur,
  onChange,
  onClearClick,
  onClose,
  onInputValueChange,
  onOpen,
  onScroll,
  openOnMount = false,
  options,
  placeholder,
  popoverCustomTopContent,
  renderGroupHeading,
  renderOption,
  renderSelectedValue: propsRenderSelectedValue,
  selectRef,
  selfContainedForm,
  size,
  suppressHelperTextBelowFieldInput = false,
  suppressOptionPadding = false,
  suppressSelectValueOnOpen = false,
  triggerButtonProps,
  value,
  variant = "default",
  warning,
}: RegrelloFormFieldSelectProps<T>) {
  const selectRefInternal = useRef<HTMLButtonElement | null>(null);
  const uuid = useId();

  const [inputValue, setInputValue] = useState(inputProps?.value ?? "");

  const commandInputRef = useRef<HTMLInputElement | null>(null);

  const hasError = error != null;
  const hasWarning = isEmphasized || warning != null;

  const handleCommandValueChange = useCallback(
    (searchQuery: string) => {
      if (!isServerSearchEnabled) {
        setInputValue(searchQuery);
      }
      onInputValueChange?.(searchQuery);
    },
    [isServerSearchEnabled, onInputValueChange],
  );

  const handleClear = useCallback(() => {
    onClearClick?.();
    onChange(null, "clear");
  }, [onChange, onClearClick]);

  const renderSelectedValue = useCallback(() => {
    const renderedValue = propsRenderSelectedValue?.(value);

    if (renderedValue != null) {
      return renderedValue;
    }

    // Fallback to default renderer if propsRenderSelectedValue or `renderedValue` are `null`-ish.
    return value != null ? (
      getOptionLabel(value)
    ) : (
      <span className="text-textPlaceholder font-normal">{placeholder}</span>
    );
  }, [getOptionLabel, placeholder, propsRenderSelectedValue, value]);

  const internalRenderOption = useCallback(
    (option: T) => {
      if (renderOption != null) {
        return renderOption(option);
      }

      return getOptionLabel(option);
    },
    [getOptionLabel, renderOption],
  );

  const internalGetOptionValue = useCallback(
    (option: T) => {
      if (getOptionValue != null) {
        return getOptionValue(option);
      }
      return getOptionLabel(option);
    },
    [getOptionLabel, getOptionValue],
  );

  const internalOptions: GroupedOptions<T> = useMemo(() => {
    let filteredOptions = options;

    if (propsFilterOptions != null) {
      filteredOptions = options.filter(propsFilterOptions);
    }

    if (groupBy != null) {
      const def = filteredOptions.reduce((acc: GroupedOptions<T>, option) => {
        const group = groupBy(option) ?? DEFAULT_GROUP;
        if (acc[group] == null) {
          const heading = renderGroupHeading?.(group) ?? group;
          const endOptions = extraEndOptionsByGroupKey != null ? extraEndOptionsByGroupKey[group] : [];
          acc[group] = { heading, options: [], extraEndOptions: endOptions };
        }
        acc[group].options.push(option);
        return acc;
      }, {});

      // Also include any end options for groups that got completely filtered out.
      if (extraEndOptionsByGroupKey != null) {
        Object.keys(extraEndOptionsByGroupKey).forEach((group) => {
          if (def[group] == null) {
            const heading = renderGroupHeading?.(group) ?? group;
            def[group] = {
              heading,
              options: [], // No options, but we need to render the group
              extraEndOptions: extraEndOptionsByGroupKey[group],
            };
          }
        });
      }

      return def;
    }

    return { [DEFAULT_GROUP]: { heading: DEFAULT_GROUP, options: filteredOptions, extraEndOptions: [] } };
  }, [extraEndOptionsByGroupKey, groupBy, options, propsFilterOptions, renderGroupHeading]);

  const isEmpty = useMemo(() => {
    return (
      options.length === 0 &&
      extraEndOptions.length === 0 &&
      Object.values(internalOptions).every((group) => group.options.length === 0 && group.extraEndOptions.length === 0)
    );
  }, [extraEndOptions.length, internalOptions, options.length]);

  const renderGroupOption = useCallback(
    (option: T) => {
      // (dosipiuk): We need to strip the quotes and new lines from value, otherwise it results with invalid query selector
      const optionValue = internalGetOptionValue(option).replaceAll(/[\r\n"]/g, "");
      const isSelected = value != null ? optionValue === internalGetOptionValue(value).replaceAll('"', "") : false;

      return (
        <RegrelloCommandItem
          key={optionValue}
          className={clsx({
            "bg-primary-soft hover:bg-primary-softHovered active:bg-primary-softPressed aria-selected:bg-primary-softHovered":
              isSelected,
          })}
          data-testid={DataTestIds.FORM_FIELD_SELECT_OPTION}
          disabled={getOptionDisabled?.(option) === true ? true : undefined}
          onSelect={() => {
            onChange(option, "select-option");
            selectRefInternal.current?.click();
          }}
          selected={isSelected}
          suppressPadding={suppressOptionPadding}
          text={internalRenderOption(option)}
          tooltip={getOptionTooltip?.(option)}
          value={optionValue}
        />
      );
    },
    [
      getOptionDisabled,
      getOptionTooltip,
      internalGetOptionValue,
      internalRenderOption,
      onChange,
      suppressOptionPadding,
      value,
    ],
  );

  const inputRefInternal = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const maybeInputElement = inputRefInternal.current;

    const handleInputRefFocus = () => {
      // (clewis): When the form library auto-focuses the hidden <input> (e.g., when attempting to
      // submit an invalid form), move focus to the select-menu trigger.
      queueMacrotask(() => selectRefInternal.current?.focus());
    };

    maybeInputElement?.addEventListener("focus", handleInputRefFocus);

    return () => {
      maybeInputElement?.removeEventListener("focus", handleInputRefFocus);
    };
  }, []);

  const { side } = useOptimalPopoverSide({
    minHeight: isLoading ? 0 : 310, // Keep in sync with max-h-77.5 on the RegrelloCommandList
    shouldRecalculate: !isLoading, // When loading finishes
    triggerRef: selectRefInternal,
  });

  useEffect(() => {
    if (openOnMount) {
      selectRefInternal.current?.click();
    }
  }, [openOnMount]);

  const selectFieldElement = useMemo(() => {
    return (
      <RegrelloPopover
        align={align}
        content={
          <RegrelloCommandRoot
            data-testid={DataTestIds.FORM_FIELD_SELECT_OPTIONS_ROOT}
            shouldFilter={isServerFiltered ? false : isFilterable}
            style={{
              // (clewis): 10px is the default collisionPadding. We need to subtract it from the maxHeight to prevent
              // the items from overflowing their container.
              maxHeight: "calc(var(--radix-popover-content-available-height) - 10px)",
              // (dosipiuk): this arbitrary value of 300px is taken from previous implementation.
              maxWidth: "max(var(--radix-popover-trigger-width), 300px)",
            }}
          >
            <RegrelloCommandInput
              ref={mergeRefs(inputRef, commandInputRef)}
              data-testid={DataTestIds.FORM_FIELD_SELECT_MENU_INPUT}
              hideFilter={!isFilterable}
              onFocus={
                suppressSelectValueOnOpen
                  ? (_) => {
                      // (jullman): Use a macrotask to ensure the input is focused before the
                      // selection range is set.
                      queueMacrotask(() => {
                        const inputElement = commandInputRef.current;
                        if (inputElement != null) {
                          const len = inputElement.value.length;
                          inputElement.setSelectionRange(len, len);
                        }
                      });
                    }
                  : undefined
              }
              onKeyDown={inputProps?.onKeyDown}
              onValueChange={handleCommandValueChange}
              placeholder={t`Search`}
              value={inputValue}
            />
            {popoverCustomTopContent}
            <RegrelloCommandList
              className="max-h-77.5" // Keep in sync with 310 on useOptimalPopoverSide
              onScroll={onScroll}
            >
              {isLoading ? (
                <RegrelloCommandLoading>
                  <div
                    className="flex justify-center items-center gap-2"
                    data-testid={DataTestIds.FORM_FIELD_SELECT_LOADING_OPTION}
                  >
                    <RegrelloSpinner size="small" />
                    <p>{t`Loading...`}</p>
                  </div>
                </RegrelloCommandLoading>
              ) : (
                <>
                  {/* Will automatically render iff there are no results. */}
                  {/*
                   * (clewis): We explicitly hide this using isEmpty because RegrelloCommandEmpty seems
                   * not to work correctly with our multi-group approach.
                   */}
                  {isEmpty && <RegrelloCommandEmpty>{t`No results`}</RegrelloCommandEmpty>}

                  {/* Options */}
                  {Object.entries(internalOptions).map(([key, groupOptions]) => {
                    if (key === DEFAULT_GROUP) {
                      return (
                        <RegrelloCommandGroup key={key}>
                          {groupOptions.options.map(renderGroupOption)}
                        </RegrelloCommandGroup>
                      );
                    }
                    return (
                      <Fragment key={key}>
                        <RegrelloCommandGroup
                          className="text-textMuted [&_[cmdk-group-heading]]:font-normal"
                          heading={groupOptions.heading}
                        >
                          {groupOptions.options.map(renderGroupOption)}
                        </RegrelloCommandGroup>
                        {groupOptions.extraEndOptions}
                      </Fragment>
                    );
                  })}

                  {isLoadingMore && <RegrelloSelectV2LoadingOption />}

                  {/* End options */}
                  {extraEndOptions}
                </>
              )}
            </RegrelloCommandList>
          </RegrelloCommandRoot>
        }
        // Disabled animations due to whacky interop with material-ui focus traps.
        // Remove this after material-ui dialog replacement with radix equivalent.
        contentProps={{
          className: "p-0 w-auto",
          disableAnimations: true,
          style: { minWidth: "var(--radix-popover-trigger-width)" },
        }}
        onOpenChange={(nextIsOpen) => {
          if (nextIsOpen) {
            onOpen?.();
          } else {
            handleCommandValueChange("");
            onClose?.();
          }
        }}
        side={side}
      >
        <RegrelloButton
          ref={mergeRefs(selectRef, selectRefInternal)}
          autoFocus={autoFocus}
          className={clsx(
            highlightButtonInOpenState && "group data-[state=open]:box-inset-2 data-[state=open]:shadow-primary-solid",
            {
              "bg-background": triggerButtonProps == null,
              "shadow-warning-solid": hasWarning,
              "shadow-danger-solid": hasError,
            },
          )}
          contentClassName={clsx("flex flex-1 justify-between items-center gap-1", {
            "overflow-visible text-textDefault font-normal": triggerButtonProps == null,
          })}
          disabled={disabled}
          fullWidth={true}
          id={uuid}
          justification="stretch"
          onBlur={onBlur}
          onKeyDown={(event) => {
            if (event.keyCode >= 48 && event.keyCode <= 90) {
              event.currentTarget.click();
            }
            if (event.key === KeyNames.BACKSPACE || event.key === KeyNames.DELETE) {
              handleClear();
            }
          }}
          size={size}
          {...(triggerButtonProps ?? {})}
          endIcon={
            triggerButtonProps?.endIcon === null ? undefined : (
              <RegrelloIcon className="transition group-data-[state=open]:rotate-180" iconName="chevron-down" />
            )
          }
          variant={triggerButtonProps?.variant ?? "outline"}
        >
          <RegrelloTooltippedText lines={1}>{renderSelectedValue()}</RegrelloTooltippedText>
          {isClearButtonEnabled ? (
            <RegrelloButton
              className={clsx("w-5 h-5", { invisible: value == null })}
              iconOnly={true}
              onClick={(event) => {
                event.preventDefault();
                handleClear();
                selectRefInternal.current?.focus();
              }}
              size="x-small"
              startIcon="close"
              variant="ghost"
            />
          ) : null}
        </RegrelloButton>
      </RegrelloPopover>
    );
  }, [
    align,
    autoFocus,
    disabled,
    extraEndOptions,
    handleClear,
    handleCommandValueChange,
    hasError,
    hasWarning,
    highlightButtonInOpenState,
    inputProps?.onKeyDown,
    inputRef,
    inputValue,
    internalOptions,
    isClearButtonEnabled,
    isEmpty,
    isFilterable,
    isLoading,
    isLoadingMore,
    isServerFiltered,
    onBlur,
    onClose,
    onOpen,
    onScroll,
    popoverCustomTopContent,
    renderGroupOption,
    renderSelectedValue,
    selectRef,
    side,
    size,
    suppressSelectValueOnOpen,
    triggerButtonProps,
    uuid,
    value,
  ]);

  const resolvedHelperText = suppressHelperTextBelowFieldInput ? undefined : helperText;

  const memoizedRenderSelectFieldElement = useCallback(() => {
    return selectFieldElement;
  }, [selectFieldElement]);

  if (variant === "spectrum" && selfContainedForm == null) {
    return (
      <RegrelloField
        dataTestId={dataTestId}
        deleted={isDeleted}
        description={infoTooltipText}
        endAdornment={endAdornment}
        errorMessage={error}
        helperText={resolvedHelperText}
        label={label}
        name={name ?? EMPTY_STRING}
        required={isRequiredAsteriskShown}
      >
        {memoizedRenderSelectFieldElement}
      </RegrelloField>
    );
  }

  return (
    <RegrelloFormFieldLayout
      className={className}
      dataTestId={dataTestId}
      endAdornment={endAdornment}
      error={isErrorMessageHidden ? undefined : error}
      helperText={resolvedHelperText}
      htmlFor={uuid}
      infoTooltipIconName={infoTooltipIconName}
      infoTooltipText={infoTooltipText}
      infoTooltipVariant={infoTooltipVariant}
      isDefaultMarginsOmitted={isDefaultMarginsOmitted}
      isDeleted={isDeleted}
      isRequiredAsteriskShown={isRequiredAsteriskShown}
      label={label}
      labelPlacement={labelPlacement}
      labelWidth={labelWidth}
      selfContainedForm={getSelfContainedFormInternal(selfContainedForm, (savedValue) =>
        savedValue === null ? <RegrelloFormFieldEmptyValueNone /> : getOptionLabel(savedValue),
      )}
      variant={variant}
      warning={warning}
    >
      {selectFieldElement}
    </RegrelloFormFieldLayout>
  );
}
